import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import _ from 'underscore';
import { slugify } from 'underscore.string';
import { Locales } from '../../strings/Locales.js';
import { PrivilegeDomains, PrivilegeLevels, PrivilegesConstants, PrivilegeTypes } from '../constants/PrivilegesConstants.js';
import { Arrays, ArraysOfObjects } from './ArraysUtils.js';
var formatPassive = function formatPassive(privileges) {
    var targetDomain = privileges[0].targetDomain;
    var formattedPrivilegeNoun = Locales.format('privilege', {
        smart_count: privileges.length
    });
    var formattedTargetDomain = Locales.formatOrDefault("".concat(targetDomain, "_DisplayName"), {
        defaultTranslation: targetDomain,
        reportMissingKey: true
    });
    var privilegeOnDomain = "".concat(formattedPrivilegeNoun, " ").concat(Locales.format('on'), " ").concat(formattedTargetDomain);
    if (privileges.length === 1) {
        var _privileges__type;
        return privileges[0].type === PrivilegeTypes.Allow ? "".concat(formattedTargetDomain, " ").concat(formattedPrivilegeNoun) : "".concat(Locales.formatOrHumanize((_privileges__type = privileges[0].type) !== null && _privileges__type !== void 0 ? _privileges__type : ''), " ").concat(privilegeOnDomain);
    } else {
        var types = _.chain(privileges).pluck('type').map(function(type) {
            return Locales.formatOrHumanize(type);
        }).value();
        return "".concat(Arrays.enumerate(types), " ").concat(privilegeOnDomain);
    }
};
var formatActive = function formatActive(privileges) {
    var targetDomain = privileges[0].targetDomain;
    var formattedTargetDomain = Locales.formatOrDefault("".concat(targetDomain, "_DisplayName"), {
        defaultTranslation: targetDomain,
        reportMissingKey: true
    }).toLowerCase();
    if (privileges.length === 1) {
        var _privileges__type;
        return privileges[0].type === PrivilegeTypes.Allow ? "".concat(formattedTargetDomain) : "".concat(Locales.formatOrHumanize((_privileges__type = privileges[0].type) !== null && _privileges__type !== void 0 ? _privileges__type : '').toLowerCase(), " ").concat(formattedTargetDomain);
    } else {
        var types = _.chain(privileges).pluck('type').map(function(type) {
            return Locales.formatOrHumanize(type).toLowerCase();
        }).value();
        return "".concat(Arrays.enumerate(types), " ").concat(formattedTargetDomain);
    }
};
var isGlobalPrivilege = function isGlobalPrivilege(privilege) {
    return privilege && Object.prototype.hasOwnProperty.call(privilege, 'level');
};
var parseBinaryPrivileges = function parseBinaryPrivileges(privilege) {
    return _.isUndefined(privilege.type) ? _object_spread_props(_object_spread({}, privilege), {
        type: PrivilegeTypes.Allow
    }) : privilege;
};
var getPrivilegeDomain = function getPrivilegeDomain(privilege) {
    var level = isGlobalPrivilege(privilege) ? privilege.level : PrivilegeLevels.Normal;
    return privilege ? "".concat(privilege.targetDomain, "_").concat(privilege.owner, "_").concat(level) : '';
};
var getPrivilegeService = function getPrivilegeService(privilege) {
    return slugify(getFormattedService(privilege));
};
var getFormattedService = function getFormattedService(privilege) {
    if (privilege) {
        var global = '';
        if (isGlobalPrivilege(privilege)) {
            global = privilege.level !== PrivilegeLevels.Normal ? '_GLOBAL' : '';
        }
        var ownerKey = "".concat(privilege.owner).concat(global, "_Service");
        return Locales.formatOrDefault("".concat(privilege.targetDomain, "_").concat(ownerKey), {
            defaultTranslation: Locales.formatOrHumanize(ownerKey, {
                defaultTranslation: privilege.owner,
                reportMissingKey: true
            })
        });
    }
    return '';
};
/**
 * @param active Formats the privileges into an active formulated sentence rather than a passive formulation
 * Example of active formulation: "*create, edit, and view sources*"
 * Example of passive formulation: "*Create, Edit, View privileges on Sources*"
 */ var toSentence = function toSentence(privileges, active) {
    return _.isEmpty(privileges) ? '' : _.compose(Arrays.enumerate, function(privilegesByDomain) {
        return _.map(privilegesByDomain, active ? formatActive : formatPassive);
    }, groupByDomain, function(dirtyPrivileges) {
        return _.map(dirtyPrivileges, parseBinaryPrivileges);
    })(privileges);
};
var groupById = function groupById(list) {
    return _.groupBy(list, 'id');
};
var groupByService = function groupByService(privileges) {
    return _.groupBy(privileges, getPrivilegeService);
};
var groupByDomain = function groupByDomain(privileges) {
    return _.mapObject(_.groupBy(privileges, getPrivilegeDomain), function(domainPrivileges) {
        return _.sortBy(domainPrivileges, 'type');
    });
};
var groupByTargetId = function groupByTargetId(privileges) {
    return _.groupBy(_.filter(privileges, function(privilege) {
        return isGranularEdit(privilege) || isGranularPush(privilege);
    }), 'targetId');
};
var hasCreatePrivilege = function hasCreatePrivilege(privileges, basePrivilege) {
    return _.any(privileges, _object_spread_props(_object_spread({}, basePrivilege), {
        type: PrivilegeTypes.Create,
        targetId: PrivilegesConstants.allTargetIds
    }));
};
var hasValidGranularPrivileges = function hasValidGranularPrivileges(privileges) {
    var granularPrivilegesGroupedByDomain = _.groupBy(_.filter(privileges, isGranularPrivilege), 'targetDomain');
    return !_.isEmpty(granularPrivilegesGroupedByDomain) && _.every(Object.keys(granularPrivilegesGroupedByDomain), function(domain) {
        var _granularPrivilegesGroupedByDomain_domain_ = granularPrivilegesGroupedByDomain[domain][0], owner = _granularPrivilegesGroupedByDomain_domain_.owner, targetDomain = _granularPrivilegesGroupedByDomain_domain_.targetDomain;
        if (targetDomain === PrivilegeDomains.PushDocument || targetDomain === PrivilegeDomains.PushIdentity) {
            // Push privilege is always valid because they do not require VIEW for target Id *
            return true;
        }
        return hasPrivilege(privileges, {
            targetDomain: targetDomain,
            owner: owner,
            type: PrivilegeTypes.View,
            targetId: PrivilegesConstants.allTargetIds
        });
    });
};
var isGranularPrivilege = function isGranularPrivilege(privilege) {
    return (isGranularEdit(privilege) || isGranularPush(privilege)) && privilege.targetId !== PrivilegesConstants.allTargetIds;
};
var isGranularDomain = function isGranularDomain(domain) {
    return _.contains(_.values(PrivilegesConstants.granularDomains), domain);
};
var isGranularEdit = function isGranularEdit(privilege) {
    return !!privilege && privilege.type === PrivilegeTypes.Edit && !_.isEmpty(privilege.targetId);
};
var isGranularPush = function isGranularPush(privilege) {
    return !!privilege && (privilege.targetDomain === PrivilegeDomains.PushDocument || privilege.targetDomain === PrivilegeDomains.PushIdentity) && !_.isEmpty(privilege.targetId);
};
var hasPrivilege = function hasPrivilege() {
    var listOfPrivileges = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [], privilegeToLookFor = arguments.length > 1 ? arguments[1] : void 0;
    return _.any(listOfPrivileges, _object_spread_props(_object_spread({}, privilegeToLookFor), {
        targetId: PrivilegesConstants.allTargetIds
    })) || _.any(listOfPrivileges, privilegeToLookFor);
};
/**
 * This privilege validation is referred as weak, because it doesn't check the targetId
 */ var hasPrivilege_weak = function hasPrivilege_weak() {
    var listOfPrivileges = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [], privilegeToLookFor = arguments.length > 1 ? arguments[1] : void 0;
    return !!_.findWhere(listOfPrivileges, _.pick(privilegeToLookFor, 'owner', 'targetDomain', 'type'));
};
var hasAllPrivileges = function hasAllPrivileges() {
    var listOfPrivileges = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [], privilegesToLookFor = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : [];
    return _.isEmpty(privilegesToLookFor) || _.every(privilegesToLookFor, function(privilege) {
        return hasPrivilege(listOfPrivileges, privilege);
    });
};
var hasAllPrivileges_weak = function hasAllPrivileges_weak() {
    var listOfPrivileges = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [], privilegesToLookFor = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : [];
    return _.isEmpty(privilegesToLookFor) || _.every(privilegesToLookFor, function(privilege) {
        return privilege.type === PrivilegeTypes.Edit ? hasPrivilege_weak(listOfPrivileges, privilege) : hasPrivilege(listOfPrivileges, privilege);
    });
};
var hasSomePrivileges = function hasSomePrivileges() {
    var listOfPrivileges = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : [], privilegesToLookFor = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : [];
    return _.isEmpty(privilegesToLookFor) || _.some(privilegesToLookFor, function(privilege) {
        return hasPrivilege(listOfPrivileges, privilege);
    });
};
var listsAreEqual = function listsAreEqual(list1, list2) {
    return _.isArray(list1) && _.isArray(list2) && list1.length === list2.length && ArraysOfObjects.difference(list1, list2).length === 0 && ArraysOfObjects.difference(list2, list1).length === 0;
};
export var PrivilegeUtils = {
    isGlobalPrivilege: isGlobalPrivilege,
    isGranularPrivilege: isGranularPrivilege,
    parseBinaryPrivileges: parseBinaryPrivileges,
    getPrivilegeDomain: getPrivilegeDomain,
    getPrivilegeService: getPrivilegeService,
    toSentence: toSentence,
    listsAreEqual: listsAreEqual,
    hasSomePrivileges: hasSomePrivileges,
    hasPrivilege_weak: hasPrivilege_weak,
    hasAllPrivileges_weak: hasAllPrivileges_weak,
    hasAllPrivileges: hasAllPrivileges,
    isGranularDomain: isGranularDomain,
    hasValidGranularPrivileges: hasValidGranularPrivileges,
    hasCreatePrivilege: hasCreatePrivilege,
    groupByService: groupByService,
    groupById: groupById,
    groupByTargetId: groupByTargetId,
    groupByDomain: groupByDomain,
    hasPrivilege: hasPrivilege,
    getFormattedService: getFormattedService
};

//# sourceMappingURL=PrivilegeUtils.js.map